import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { questionHelperAPI, handleAPIError } from '../services/api';
import { topicsForPaper, availableMarks } from '../lib/qhSession';

const StatusBadge = ({ value }) => {
  const map = {
    PASS: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    WARNING: 'bg-amber-100 text-amber-800 border-amber-300',
    FAIL: 'bg-red-100 text-red-800 border-red-300',
  };
  return <Badge className={`border ${map[value] || 'bg-slate-100 text-slate-700'}`} data-testid={`status-${value}`}>{value}</Badge>;
};

const QuestionHelperAdmin = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [practice, setPractice] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      await questionHelperAPI.seed();
      const [data, practiceRes] = await Promise.all([
        questionHelperAPI.getValidation(),
        questionHelperAPI.getPractice(),
      ]);
      setReport(data);
      setPractice(practiceRes.questions || []);
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-600"><Loader2 className="w-6 h-6 animate-spin" /> Running validation…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="admin-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Button>
          <Button variant="outline" onClick={load} data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Re-run</Button>
        </div>

        {error && <Card><CardContent className="p-4 text-red-700" data-testid="admin-error">{error}</CardContent></Card>}

        {report && (
          <>
            <Card className="border-0 shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl">Question Helper — Admin Test View</CardTitle>
                  <StatusBadge value={report.overall === 'PASS' ? 'PASS' : 'WARNING'} />
                </div>
                <p className="text-sm text-slate-500">Generated {new Date(report.generated_at).toLocaleString()}</p>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center" data-testid="admin-count-questions">
                  <div className="text-3xl font-bold text-blue-700">{report.counts.questions}</div>
                  <div className="text-xs text-slate-600">Questions (exp {report.expected.questions})</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center" data-testid="admin-count-model-answers">
                  <div className="text-3xl font-bold text-emerald-700">{report.counts.model_answers}</div>
                  <div className="text-xs text-slate-600">Model answers (exp {report.expected.model_answers})</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center" data-testid="admin-count-segments">
                  <div className="text-3xl font-bold text-purple-700">{report.counts.segments}</div>
                  <div className="text-xs text-slate-600">Answer segments</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center" data-testid="admin-count-categories">
                  <div className="text-3xl font-bold text-amber-700">{report.counts.annotation_categories}</div>
                  <div className="text-xs text-slate-600">Annotation categories</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow">
              <CardHeader><CardTitle className="text-lg">Validation checks</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(report.checks).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b py-2 last:border-0" data-testid={`admin-check-${k}`}>
                    <span className="text-slate-700 capitalize">{k.replace(/_/g, ' ')}</span>
                    <StatusBadge value={v} />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 shadow">
              <CardHeader><CardTitle className="text-lg">Segment reconstruction</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {report.reconstruction.map((r) => (
                  <div key={r.answer_id} className="flex items-center justify-between border-b py-2 last:border-0 text-sm" data-testid={`admin-recon-${r.answer_id}`}>
                    <span className="font-mono text-slate-700">{r.answer_id}</span>
                    <span className="text-slate-500">{r.segments} segments · {r.length} chars</span>
                    <StatusBadge value={r.reconstructable ? 'PASS' : 'FAIL'} />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 shadow">
              <CardHeader><CardTitle className="text-lg">Integrity issues</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between" data-testid="admin-orphan-ma"><span>Orphaned model answers</span><span>{report.orphan_model_answers.length}</span></div>
                <div className="flex justify-between" data-testid="admin-orphan-seg"><span>Orphaned segments</span><span>{report.orphan_segments.length}</span></div>
                <div className="flex justify-between" data-testid="admin-invalid-cat"><span>Invalid-category segments</span><span>{report.invalid_category_segments.length}</span></div>
                <div className="flex justify-between" data-testid="admin-mark-errors"><span>Mark-range errors</span><span>{report.mark_range_errors.length}</span></div>
                <div className="flex justify-between" data-testid="admin-wrong-ma"><span>Questions without exactly 3 model answers</span><span>{report.questions_with_wrong_model_answer_count.length}</span></div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow" data-testid="admin-setup-test">
              <CardHeader><CardTitle className="text-lg">Setup-page filter test</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between font-semibold" data-testid="admin-setup-total">
                  <span>Active questions recognised</span><span>{practice.length}</span>
                </div>
                {['P1', 'P2', 'P4'].map((pc) => {
                  const qs = practice.filter((q) => q.paper_code === pc);
                  const expected = { P1: 36, P2: 36, P4: 28 }[pc];
                  const grp = topicsForPaper(practice, pc)[0];
                  const allTopics = grp ? grp.topics : [];
                  const marks = availableMarks(practice, pc, allTopics);
                  return (
                    <div key={pc} className="border rounded-lg p-3" data-testid={`admin-setup-${pc}`}>
                      <div className="flex justify-between font-medium">
                        <span>{pc} questions</span>
                        <span>{qs.length} {qs.length === expected ? '✓' : `(expected ${expected})`}</span>
                      </div>
                      <div className="text-slate-500 mt-1">Topics: {allTopics.join(', ')}</div>
                      <div className="text-slate-500">Marks present: {marks.join(', ')}</div>
                    </div>
                  );
                })}
                <div className="text-slate-500" data-testid="admin-setup-cmd">
                  Command terms (dynamic): {[...new Set(practice.map((q) => q.command_term))].sort().join(', ')}
                </div>
                <div className="text-slate-500">
                  Case-study questions: {practice.filter((q) => (q.case_study || '').trim()).length} ·
                  Non-case-study: {practice.filter((q) => !(q.case_study || '').trim()).length}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionHelperAdmin;
